import errorReporter from '@app/errorReporter';
import { Button, Flex, Form, Input, Title } from '@bb/elements-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateFile } from 'api/AssetController';
import { Icon, message, Modal } from 'components';
import useBreakpoint from 'hooks/useBreakpoint';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ASSET_MODAL_LG_WIDTH } from '../constants';

export interface VideoFile {
  id: string;
  name: string;
  embedUrl?: string;
}

export interface VideoPlayerModalProps {
  visible: boolean;
  file?: VideoFile;
  isAdmin: boolean;
  onClose: () => void;
}

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({
  visible,
  file,
  isAdmin,
  onClose,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const screen = useBreakpoint();
  const isLessThanLarge = !screen.lg;
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (visible && file?.name) {
      form.setFieldsValue({ name: file.name });
      setIsEditing(false);
    }
  }, [visible, file, form]);

  // Update filename mutation
  const updateFileNameMutation = useMutation({
    mutationFn: (newFileName: string) => {
      if (!file?.id) throw new Error('No file ID');
      return updateFile(file.id, newFileName);
    },
    onSuccess: () => {
      message.success('Video name updated successfully');
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      setIsEditing(false);
      onClose();
    },
    onError: () => {
      message.error('Failed to update video name');
      setIsEditing(true);
    },
  });

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      // When entering edit mode, reset to current file name
      form.setFieldsValue({ name: file?.name || '' });
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      updateFileNameMutation.mutate(values.name);
    } catch (error) {
      errorReporter.error('Failed to save video name:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.resetFields();
    onClose();
  };

  const loading = updateFileNameMutation.isPending;

  return (
    <Modal
      centered
      width={isLessThanLarge ? '85%' : ASSET_MODAL_LG_WIDTH}
      destroyOnClose
      closeIcon={false}
      closable={false}
      title={
        <StyledHeader>
          <StyledTitleContainer>
            {isAdmin && isEditing ? (
              <Form
                form={form}
                layout="vertical"
                dataCy="video-name-form"
                onFinish={handleSave}
              >
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: 'Please enter a file name.' },
                  ]}
                  style={{ margin: 0 }}
                >
                  <Input dataCy="video-player-modal-name-input" />
                </Form.Item>
              </Form>
            ) : (
              <StyledModalTitle
                dataCy="video-player-modal-title"
                level={3}
                thickness="normal"
              >
                {file?.name}
              </StyledModalTitle>
            )}
          </StyledTitleContainer>
          {isAdmin && (
            <StyledIconButton
              onClick={handleToggleEdit}
              data-cy="video-player-modal-edit-button"
            >
              <Icon type="antdSettingOutlined" height={20} />
            </StyledIconButton>
          )}
        </StyledHeader>
      }
      open={visible}
      onCancel={handleCancel}
      confirmLoading={loading}
      footer={[
        <Flex
          key="1-video-player-modal-footer-container"
          align="center"
          justify="flex-end"
        >
          {isEditing && (
            <Button
              key="1-save-button"
              minWidth={65}
              size="small"
              type="primary"
              onClick={handleSave}
              loading={loading}
              dataCy="video-player-modal-save-button"
            >
              Save
            </Button>
          )}
          <Button
            key="2-close-button"
            minWidth={65}
            size="small"
            type="secondary"
            color="default"
            onClick={handleCancel}
            dataCy="video-player-modal-close-button"
          >
            Close
          </Button>
        </Flex>,
      ]}
    >
      {file?.embedUrl && (
        <StyledIframeContainer>
          <StyledIframe
            src={file.embedUrl}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={file.name}
          />
        </StyledIframeContainer>
      )}
    </Modal>
  );
};

export default VideoPlayerModal;

const StyledModalTitle = styled(Title)`
  && {
    margin: 0;
  }
`;

const StyledHeader = styled(Flex)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StyledTitleContainer = styled.div`
  flex: 1;
  width: 100%;
`;

const StyledIconButton = styled.span`
  cursor: pointer;
  padding: 0 10px;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

const StyledIframeContainer = styled.div`
  position: relative;
  height: 0;
  width: 100%;
  padding-bottom: 56.25%;
`;

const StyledIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;
